/**
 * TooltipFilings
 * ------------------------------------------------------------------
 * Canonical, compliance-safe explanations for SEC filing types.
 *
 * DESIGN PRINCIPLES
 * - Descriptive only (no predictive or advisory language)
 * - Neutral regulatory tone
 * - Tooltip-friendly length
 * - Centralized single source of truth
 * - Robust to filing format variants
 *
 * USAGE
 * import { getFilingTooltip } from '@/utils/TooltipFilings';
 * ------------------------------------------------------------------
 */

/**
 * INTERNAL: normalize filing codes coming from APIs
 * Examples:
 *  - "10-K"   -> "10K"
 *  - "SC 13G" -> "SC13G"
 *  - "s-1"    -> "S1"
 */
const normalizeForm = (form) =>
  String(form || '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/-/g, '');

/**
 * Canonical filing tooltip text
 */
export const FILING_TOOLTIPS = Object.freeze({

  /* ───────────────────────────
     CAPITAL MARKETS / OFFERINGS
     ─────────────────────────── */

  FWP: `
Free Writing Prospectus (FWP)

Supplemental disclosure used during a securities offering.
Often includes investor presentations, deal summaries,
or clarifying information related to a capital raise.
`,

  '424B1': `
Prospectus filed following initial registration.
Contains offering details prior to final pricing.
`,

  '424B2': `
Final prospectus for a securities offering.
Includes final pricing and allocation information.
`,

  '424B3': `
Prospectus supplement or resale prospectus.
Common in secondary offerings or selling shareholder transactions.
`,

  '424B4': `
Prospectus related to mergers or exchange offers.
Describes transaction structure and terms.
`,

  S1: `
Form S-1 Registration Statement.

Filed when a company plans to go public
or issue new securities to the public.
`,

  S3: `
Form S-3 Registration Statement.

Simplified registration for eligible public companies.
Often used for shelf or secondary offerings.
`,

  S4: `
Form S-4 Registration Statement.

Used in mergers, acquisitions,
or exchange offer transactions.
`,

  S8: `
Form S-8 Registration Statement.

Registers securities issued to employees
under compensation or incentive plans.
`,

  /* ───────────────────────────
     PERIODIC REPORTING
     ─────────────────────────── */

  '10K': `
Form 10-K Annual Report.

Comprehensive annual disclosure including
audited financial statements and risk factors.
`,

  '10Q': `
Form 10-Q Quarterly Report.

Unaudited quarterly financial updates
and management discussion.
`,

  '8K': `
Form 8-K Current Report.

Filed to disclose material events
outside regular reporting cycles.
`,

  '20F': `
Form 20-F Annual Report.

Annual disclosure for foreign private issuers,
similar in scope to Form 10-K.
`,

  '6K': `
Form 6-K Report.

Filed by foreign issuers to report
material information released outside the U.S.
`,

  /* ───────────────────────────
     OWNERSHIP & CONTROL
     ─────────────────────────── */

  '13D': `
Schedule 13D.

Filed when an investor acquires more than 5%
with potential intent to influence control.
`,

  '13G': `
Schedule 13G.

Short-form ownership disclosure
for passive investors exceeding 5%.
`,

  'SC13D': `
Schedule 13D.

Disclosure of significant ownership
and potential control intent.
`,

  'SC13G': `
Schedule 13G.

Passive ownership disclosure
for large shareholders.
`,

  '13F': `
Form 13F Holdings Report.

Quarterly disclosure of equity holdings
by institutional investment managers.
`,

  /* ───────────────────────────
     INSIDER TRANSACTIONS
     ─────────────────────────── */

  '3': `
Form 3.

Initial statement of beneficial ownership
for company insiders.
`,

  '4': `
Form 4.

Reports insider purchases or sales
of company securities.
`,

  '5': `
Form 5.

Annual summary of insider transactions
not previously reported.
`,

  '144': `
Form 144 Notice.

Filed when insiders plan to sell
restricted or control securities.
`,

  /* ───────────────────────────
     SHAREHOLDER GOVERNANCE
     ─────────────────────────── */

  'DEF14A': `
Definitive Proxy Statement.

Provides voting materials for shareholder meetings,
including executive compensation and governance matters.
`,

  /* ───────────────────────────
     FALLBACK
     ─────────────────────────── */

  UNKNOWN: `
SEC filing.

Regulatory disclosure filed with the
U.S. Securities and Exchange Commission.
`
});

/**
 * Public helper used by UI components
 *
 * @param {string} form - raw filing form (e.g. "10-K", "SC 13G")
 * @param {Function} [t] - i18next translate function; falls back to the
 *   English text above (FILING_TOOLTIPS) when not provided.
 * @returns {string} tooltip text
 */
export const getFilingTooltip = (form, t) => {
  const key = normalizeForm(form);
  const fallback = FILING_TOOLTIPS[key] || FILING_TOOLTIPS.UNKNOWN;
  return t ? t(`dashboard.filingTooltips.${key}`, { defaultValue: fallback }) : fallback;
};
